// src/lib/blog/config/index.ts

import type { BlogConfig } from '~/lib/blog/types/blog.types'
import { AUTHORS } from '~/lib/blog/config/authors'
import { DEFAULTS } from '~/lib/blog/config/defaults'
import { TOPICS } from '~/lib/blog/config/topics'

// `DEFAULTS`, `AUTHORS`, and `TOPICS` now live in their own modules and are
// re-exported here so the `~/lib/blog/config` barrel is unchanged. The split
// lets the sitemap floor `/blog/authors/*` and `/blog/topics*` `<lastmod>` on a
// PER-SECTION git mtime (authors.ts / topics.ts) rather than this shared file —
// so editing one author's bio bumps only author pages, not the whole blog.
export { AUTHORS, authorSlugFromHref, findAuthorBySlug } from '~/lib/blog/config/authors'
export { DEFAULTS } from '~/lib/blog/config/defaults'
export { TOPICS } from '~/lib/blog/config/topics'

/**
 * SEO Configuration
 */
export const SEO_CONFIG = {
  title: 'AdSights Blog | Creative Analytics & Digital Advertising Insights',
  description: 'Master digital advertising with expert insights on creative analytics, ad performance optimization, and data-driven strategies. Get actionable tips for creative testing, AI in advertising, and campaign management.',
  keywords: [
    'creative analytics',
    'ad optimization',
    'creative testing',
    'digital advertising',
    'ad performance',
    'creative strategy',
    'AI in advertising',
    'social media advertising',
    'ad tech',
    'media buying',
    'campaign management',
    'performance marketing'
  ],
  authors: [{ name: 'AdSights Team', url: `${DEFAULTS.SITE_URL}/about` }],
  openGraph: {
    title: 'AdSights Blog | Creative Analytics & Digital Advertising Insights',
    description: 'Master digital advertising with expert insights on creative analytics, ad performance optimization, and data-driven strategies.',
    type: 'website',
    url: `${DEFAULTS.SITE_URL}/blog`,
    siteName: 'AdSights',
    locale: 'en_US',
    images: [{
      url: DEFAULTS.BLOG_IMAGE,
      width: 1200,
      height: 630,
      alt: 'AdSights Blog - Creative Analytics & Digital Advertising Insights',
      type: 'image/webp'
    }]
  },
  twitter: {
    card: 'summary_large_image',
    site: '@adsightsai',
    creator: '@adsightsai',
    title: 'AdSights Blog | Creative Analytics & Digital Advertising Insights',
    description: 'Master digital advertising with expert insights on creative analytics, ad performance optimization, and data-driven strategies.',
    images: [{
      url: DEFAULTS.BLOG_IMAGE,
      alt: 'AdSights Blog - Creative Analytics & Digital Advertising Insights'
    }]
  },
  alternates: {
    canonical: `${DEFAULTS.SITE_URL}/blog`,
    languages: {
      'en-US': `${DEFAULTS.SITE_URL}/blog`
    }
  },
  robots: {
    index: true,
    follow: true,
    'max-snippet': -1,
    'max-image-preview': 'large',
    'max-video-preview': -1
  }
}

/**
 * Page-specific SEO configurations
 */
export const PAGE_SEO = {
  // Main blog pages
  blog: {
    home: {
      title: 'AdSights Blog: Creative Analytics & Ad Insights',
      description: 'Elevate your digital advertising with expert insights on creative analytics, ad performance optimization, and data-driven strategies from AdSights.',
      keywords: ['creative analytics blog', 'digital advertising insights', 'ad performance optimization', 'creative testing guides', 'AI in advertising', 'data-driven marketing strategies']
    },
    topics: {
      title: 'Blog Topics | Creative Analytics & Advertising',
      description: 'Explore digital advertising topics: creative analytics, ad performance optimization, creative testing, AI in advertising, and data-driven strategy.',
      keywords: ['digital advertising topics', 'creative analytics categories', 'marketing performance topics', 'ad optimization categories', 'advertising strategy guides', 'article categories', 'blog categories']
    },
    authors: {
      title: 'Blog Authors | Advertising & Creative Analytics Experts',
      description: 'Meet the digital advertising experts, data scientists, and creative strategists behind AdSights insights on creative analytics and ad performance.',
      keywords: ['digital advertising experts', 'creative analytics specialists', 'marketing data scientists', 'AdSights team', 'advertising thought leaders', 'industry experts']
    },
    tags: {
      title: 'Blog Tags | Digital Advertising Topics & Trends',
      description: 'Browse topic tags for targeted insights on creative analytics, ad performance metrics, AI in advertising, and digital marketing strategy.',
      keywords: ['digital advertising tags', 'creative analytics tags', 'performance marketing tags', 'advertising strategy tags', 'marketing optimization guides', 'article tags', 'blog tags']
    },
    archive: {
      title: 'Blog Archive | Digital Advertising Insights Library',
      description: 'Browse our library of digital advertising insights, creative analytics research, and ad performance optimization strategies, organized by date.',
      keywords: ['digital advertising library', 'creative analytics archive', 'marketing insights collection', 'advertising strategy resources', 'performance optimization guides', 'advertising blog archive', 'marketing articles library']
    },
    search: {
      title: 'Search AdSights Blog | Creative Analytics Insights',
      description: 'Search AdSights for creative analytics guides, ad performance optimization techniques, creative testing insights, and advertising strategies.',
      keywords: ['creative analytics search', 'find advertising insights', 'digital marketing resources', 'performance optimization guides', 'advertising strategy search', 'article search', 'blog search', 'search marketing resources']
    },
    notFound: {
      title: 'Page Not Found | AdSights Blog',
      description: 'The page you are looking for does not exist. Browse our latest articles about creative analytics and digital advertising.',
    }
  },
  // Tag page template
  tagPage: (tag: string) => ({
    title: `${tag} | Digital Advertising Insights & Strategies | Blog`,
    description: `Expert articles about ${tag} in digital advertising: data-driven strategies, best practices, and optimization techniques to improve performance.`,
    keywords: [tag, `${tag} strategies`, `${tag} optimization`, `${tag} best practices`, `${tag} in advertising`, `${tag} marketing`]
  }),
  // Archive year page template
  archiveYearPage: (year: string) => ({
    title: `${year} Digital Advertising Insights | Blog Archive`,
    description: `Browse our ${year} collection of expert articles on creative analytics, ad performance, and digital advertising strategies.`,
    keywords: [`${year} advertising trends`, `${year} marketing insights`, `${year} creative analytics`, `${year} digital advertising`, `${year} ad performance`]
  })
}

/**
 * Special cases for formatting terms and tags
 * Used for proper capitalization and acronym handling
 */
export const SPECIAL_CASES: Record<string, string> = {
  // Common acronyms
  'ai': 'AI',
  // `ab` only ever appears as the `ab-testing` tag segment in this domain;
  // render it as the standard "A/B" so the tag shows "A/B Testing" instead of
  // the title-cased "Ab Testing".
  'ab': 'A/B',
  'ml': 'ML',
  'ar': 'AR',
  'vr': 'VR',
  'roi': 'ROI',
  'roas': 'ROAS',
  'mer': 'MER',
  'amer': 'aMER',
  'nmer': 'nMER',
  'cac': 'CAC',
  'ncac': 'nCAC',
  'aov': 'AOV',
  'ltv': 'LTV',
  'seo': 'SEO',
  'ctr': 'CTR',
  'cpc': 'CPC',
  'cpm': 'CPM',
  'cpa': 'CPA',
  'cpt': 'CPT',
  'cpv': 'CPV',
  'cpl': 'CPL',
  'cpe': 'CPE',
  'b2b': 'B2B',
  'b2c': 'B2C',
  'dtc': 'DTC',
  'ui': 'UI',
  'ux': 'UX',
  'api': 'API',
  'url': 'URL',
  'html': 'HTML',
  'css': 'CSS',
  'mdx': 'MDX',
  'ssr': 'SSR',
  'csv': 'CSV',
  'png': 'PNG',
  'pdf': 'PDF',
  'faq': 'FAQ',
  'json': 'JSON',
  'ld': 'LD',
  'llm': 'LLM',
  'gsc': 'GSC',
  // Video / ad-platform acronyms (PR #104 — Vcr/Vtr were showing as title-cased instead of preserved)
  'vcr': 'VCR',
  'vtr': 'VTR',
  'ctv': 'CTV',
  'ott': 'OTT',
  'og': 'OG',
  'cta': 'CTA',
  'kpi': 'KPI',
  'crm': 'CRM',
  'cms': 'CMS',
  'sdk': 'SDK',
  'sns': 'SNS',
  'sso': 'SSO',
  'mfa': 'MFA',

  // Common terms that should remain lowercase
  'and': 'and',
  'or': 'or',
  'the': 'the',
  'in': 'in',
  'on': 'on',
  'at': 'at',
  'to': 'to',
  'for': 'for',
  'of': 'of',
  'with': 'with',
  'by': 'by',
  'vs': 'vs',

  // Brand names + product names — preserve their canonical capitalization (PR #104)
  'adsights': 'AdSights',
  'thruplay': 'ThruPlay',
  'tiktok': 'TikTok',
  'youtube': 'YouTube',
  'imessage': 'iMessage',
  'iphone': 'iPhone',
  'ipad': 'iPad',
  'ios': 'iOS',
  'macos': 'macOS',
  'github': 'GitHub',
  'linkedin': 'LinkedIn',
  'youtubekids': 'YouTube Kids',
  'whatsapp': 'WhatsApp',
  'snapchat': 'Snapchat',
  'pinterest': 'Pinterest',
  'meta': 'Meta',
  'reels': 'Reels',
  'shorts': 'Shorts',
  'stories': 'Stories',
  'trueview': 'TrueView',
}

/**
 * Blog configuration
 */
export const BLOG_CONFIG: BlogConfig = {
  title: 'AdSights Blog',
  description: 'Discover expert insights, strategies, and best practices for optimizing your ad creative performance. Learn about creative testing, analytics, and data-driven advertising strategies.',
  postsPerPage: 12,
  featuredPostsCount: 3,
  topics: TOPICS,
  defaultTopic: 'creative-analytics',
  authors: AUTHORS,
  sitemap: {
    staticRoutes: [
      {
        path: '/blog',
        priority: 1.0,
        changefreq: 'daily'
      },
      {
        path: '/blog/topics',
        priority: 0.8,
        changefreq: 'weekly'
      },
      {
        path: '/blog/authors',
        priority: 0.6,
        changefreq: 'monthly'
      },
      {
        path: '/blog/tags',
        priority: 0.8,
        changefreq: 'weekly'
      },
      {
        path: '/blog/archive',
        priority: 0.7,
        changefreq: 'monthly'
      }
      // `/blog/search` intentionally omitted — default search UI is noindex;
      // submitting it in sitemap.xml contradicts robots (see blogSitemapProvider).
    ],
    // Posts only — taxonomy and archive URLs are emitted once by
    // `blogSitemapProvider` with correct priorities and thin-tag filtering.
    getDynamicRoutes: (posts) =>
      posts
        .filter((post) => {
          if (post.status !== 'published') return false
          // Floor BOTH dates to the start of the day before comparing — matching
          // `isPostVisible` (blog/utils/content.ts), which is what actually gates
          // whether the post page renders. Comparing full timestamps here while
          // visibility floors to the day means a post published "today" at, say,
          // 16:00 UTC is reachable yet omitted from sitemap.xml on any build
          // earlier that day — invisible to crawlers until the next deploy.
          // (Can't import isPostVisible: config ← content ← config would cycle.)
          const pub = new Date(post.date)
          pub.setHours(0, 0, 0, 0)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          return pub <= today
        })
        .map((post) => ({
          path: post.href,
          lastModified: post.seo.openGraph?.modifiedTime ?? post.date,
          // The publish date, threaded through so the sitemap provider can floor
          // `<lastmod> ≥ datePublished` (a post can't be modified before it was
          // published) — see `blogSitemapProvider`.
          published: post.date,
          priority: post.featured ? 0.9 : 0.7,
          changefreq: 'monthly' as const,
        })),
  }
}

/**
 * Constants
 */
export const BLOG_DIR = 'src/content/blog'
export const DEFAULT_IMAGE = DEFAULTS.BLOG_IMAGE
export const WORDS_PER_MINUTE = DEFAULTS.WORDS_PER_MINUTE

/**
 * Re-export routes and metadata configuration
 */
export { BLOG_ROUTES, BLOG_META, BLOG_CONFIG as BlogConfigUtils } from '~/lib/blog/config/routes'

/**
 * Re-export tags configuration
 */
export { TAGS } from '~/lib/blog/config/tags'

/**
 * Revalidation Configuration
 * Controls how frequently different types of content are revalidated
 */
export const REVALIDATION = {
  // Blog post content - longer cache as content changes less frequently
  POST: {
    DEFAULT: 3600, // 1 hour
    PUBLISHED: 3600 * 24, // 24 hours for published posts
    DRAFT: 60, // 1 minute for drafts
  },
  // List pages - shorter cache as they aggregate content
  LISTS: {
    DEFAULT: 1800, // 30 minutes
    FEATURED: 900, // 15 minutes for featured content
    TRENDING: 300, // 5 minutes for trending content
  },
  // Archive pages - longest cache as they rarely change
  ARCHIVE: 3600 * 24, // 24 hours
  // Author/Topic pages - medium cache
  TAXONOMY: 3600 * 2, // 2 hours
} as const
